import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { ServicesDialog } from 'src/components/Dialogs/ServicesDialog/ServicesDialog';
import { Modal } from 'src/components/Modal/ServicesModal/Modal';
import { RightServicesDrawer } from 'src/components/RightDrawer/RightServicesDrawer';
import { ServicesSearchTypes } from 'src/components/ToggleBox/ToggleBox.types';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { ChevronDown, ChevronUp, PlusIcon, SearchIcon, } from 'src/Icons';
import { getCategoryTemplates } from 'src/store/reducers/categoryTemplatesSlice';
import {
  initialServicesDialogParams,
  setServicesDialogParams,
  showServicesDialog,
} from 'src/store/reducers/dialogSlice';
import { closeDrawer } from 'src/store/reducers/drawerSlice';
import { getServiceTemplates } from 'src/store/reducers/serviceTemplatesSlice';
import { resetSelectedRow } from 'src/store/reducers/tableSlice';

import { EmptyPageState, MainPage, SubCategoriesTable, SubmitButton, ToggleBox } from '../../components';
import { SortByMenuStyleProps } from '../CommonComponentsStyles';

import { CustomActionCardMenuActionType } from './components/CustomActionCardMenu/CustomActionCardMenu.types';
import { SelectedCategoryHeader } from './components/SelectedCategoryHeader/SelectedCategoryHeader';
import { CategoriesListSection } from './components';
import { SortTypes } from './ServicesScreen.types';

export function ServicesScreen () {
  const dispatch = useAppDispatch();

  const { isServicesDialogOpen } = useAppSelector((state) => state.dialog);
  const { categories, isCategoriesLoading } = useAppSelector((state) => state.categoryTemplates);

  const [selectedToggleBox, setSelectedToggleBox] =
    useState<string>(ServicesSearchTypes.SERVICE);
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<SortTypes>(SortTypes.ID);
  const [isSortSelectOpen, setIsSortSelectOpen] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [actionType, setActionType] = useState(
    CustomActionCardMenuActionType.EditCategory
  );

  const getSortedCategories = useCallback((categories: any) => {
    if (sortBy === SortTypes.NAME) {
      return categories.slice().sort((a: any, b: any) => a.title.localeCompare(b.title, 0, {numeric: false}))
    } else {
      return _.orderBy(categories, ['id'], ['asc']);
    }
  }, [sortBy]);

  const getSortedSelectedCategory = useCallback((category: any) => {
    if (sortBy === SortTypes.NAME) {
      return {
        ...category,
        services: category?.services?.slice()?.sort(
          (a: any, b: any) => a.title.localeCompare(b.title, 0, {numeric: false})
        ),
        children: category?.children?.map((cat: any) => {
          return {
            ...cat,
            services: cat.services?.slice()?.sort(
              (a: any, b: any) => a.title.localeCompare(b.title, 0, {numeric: false})
            )
          }
        })
      }
    } else if (sortBy === SortTypes.DURATION) {
      return {
        ...category,
        services: _.orderBy(category.services, ['duration'], ['asc']),
        children: category?.children.map((cat: any) => {
          return {
            ...cat,
            services: _.orderBy(cat.services, ['duration'], ['asc'])
          }
        })
      }
    } else {
      return {
        ...category,
        services: _.orderBy(category.services, ['id'], ['asc']),
        children: category?.children?.map((cat: any) => {
          return {
            ...cat,
            services: _.orderBy(cat.services, ['id'], ['asc'])
          }
        })
      }
    }
  }, [sortBy]);


  const [selectedCategory, setSelectedCategory] = useState<any>();
  const [filteredSelectedCategory, setFilteredSelectedCategory] = useState<any>(selectedCategory);
  const [filteredCategories, setFilteredCategories] = useState<any>(getSortedCategories(categories));

  const handleChange = useCallback((nextSelected: string) => {
    setSelectedToggleBox(nextSelected);
  }, [setSelectedToggleBox]);

  const handleChangeSortBy = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SortTypes);
  };

  useEffect(() => {
    if (!selectedCategory) {
      setFilteredCategories(getSortedCategories(filteredCategories));
    } else {
      setFilteredSelectedCategory(getSortedSelectedCategory(filteredSelectedCategory))
    }
  }, [sortBy]);

  const resetFilteredCategories = useCallback(() => {
    setFilteredCategories(getSortedCategories(categories));
    setFilteredSelectedCategory(getSortedSelectedCategory(selectedCategory || {}));
  }, [categories, selectedCategory, sortBy]);

  const handleSearchClick = useCallback(() => {
    if (!searchInput.length) {
      resetFilteredCategories();
      return;
    }

    const searchRegExp = new RegExp(searchInput, 'gi');

    const searchByServices = (services: any) => {
      return services.some((service: any) => {
        return searchRegExp.test(service.title);
      });
    };

    const searchBySubCategories = (subCategories: any) => {
      return subCategories.some((cat: any) => {
        return searchRegExp.test(cat.title);
      });
    };

    if (!selectedCategory) {
      const newFilteredCategories = categories.filter((category: any) => {
        switch (selectedToggleBox) {
          case ServicesSearchTypes.SERVICE:
            return searchByServices(category.services) || category.children.some((child: any) => {
              return searchByServices(child.services)
            });
          case ServicesSearchTypes.CATEGORY:
            return searchRegExp.test(category.title);
          case ServicesSearchTypes.SUBCATEGORY:
            return searchBySubCategories(category.children);
          default:
            return false;
        }
      });

      setFilteredCategories(getSortedCategories(newFilteredCategories));
    } else {
      let newFilteredSelectedCategory;

      if (selectedToggleBox === ServicesSearchTypes.SERVICE) {
        newFilteredSelectedCategory = {
          ...selectedCategory,
          services: selectedCategory.services.filter((service: any) => {
            return searchRegExp.test(service.title);
          }),
          children: selectedCategory.children.map((cat: any) => {
            return {
              ...cat,
              services: cat.services.filter((service: any) => {
                return searchRegExp.test(service.title);
              })
            }
          })
        }
      } else {
        newFilteredSelectedCategory = {
          ...selectedCategory,
          children: selectedCategory.children.filter((cat: any) => {
            return searchRegExp.test(cat.title);
          })
        }
      }

      setFilteredSelectedCategory(getSortedSelectedCategory(newFilteredSelectedCategory));
    }
  }, [
    categories,
    searchInput,
    selectedToggleBox,
    setFilteredCategories,
    selectedCategory,
    setFilteredSelectedCategory,
  ]);

  const handleOpenServicesDialog = () => {
    dispatch(setServicesDialogParams({
      step: 0,
      maxAvailableStep: 0,
      isEdit: false,
      data: {
        category: selectedCategory,
        ...initialServicesDialogParams.data
      },
    }));
    dispatch(showServicesDialog());
  };

  const handleOpenCreateCategoryModal = () => {
    setActionType(CustomActionCardMenuActionType.CreateCategory);
    setIsModalOpen(true);
  };

  const handleCategorySelect = (cat: any) => {
    setSearchInput('');
    if (selectedToggleBox === ServicesSearchTypes.CATEGORY) {
      setSelectedToggleBox(ServicesSearchTypes.SERVICE);
    }
    resetFilteredCategories();
    setSelectedCategory(cat);
    setFilteredSelectedCategory(getSortedSelectedCategory(cat));
  }

  const handleResetSelectedCategory = () => {
    setSearchInput('');
    dispatch(resetSelectedRow());
    dispatch(closeDrawer());

    if (sortBy === SortTypes.DURATION) {
      setSortBy(SortTypes.ID);
    }

    resetFilteredCategories();
    setFilteredSelectedCategory(null);
    setSelectedCategory(null);
  }

  const addButtonText = "Add category";

  useEffect(() => {
    dispatch(getCategoryTemplates());
    dispatch(getServiceTemplates());
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      const updatedSelectedCategory = categories.find((el: any) => el.id === selectedCategory.id);

      setSelectedCategory(updatedSelectedCategory);

      if (!searchInput) {
        setFilteredSelectedCategory(getSortedSelectedCategory(updatedSelectedCategory))
      } else {
        setTimeout(() => {
          handleSearchClick();
        }, 10)
      }
    } else {
      handleSearchClick();
    }

  }, [categories]);

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        typeOfServiceId={actionType}
        onSubmit={() => {
          setIsModalOpen(false);
          dispatch(getCategoryTemplates());
        }}
        onClose={() => setIsModalOpen(false)}
      />
      <MainPage
        title="Services"
        shouldShowHeader={!categories.length && !isCategoriesLoading}
        addButtonText={addButtonText}
        isDialogOpen={isServicesDialogOpen}
        isShouldShowEmptyState={!categories.length && !isCategoriesLoading}
        onAddButtonClick={handleOpenServicesDialog}
        DialogComp={<ServicesDialog isOpen={isServicesDialogOpen} />}
        DrawerComp={<RightServicesDrawer />}
        EmptyStateComp={
          <EmptyPageState
            image="/images/empty-page-states/services.png"
            title="It seems there are no services configured in the system."
            subTitle="To start organizing services, you can create new services, assign them to appropriate categories and subcategories, set prices and durations, and define ranks for different levels of expertise."
            buttonText={addButtonText}
            onButtonClick={handleOpenCreateCategoryModal}
          />
        }
      >
        <>
          <Box
            display="flex"
            flexDirection="row"
            flex={1}
            justifyContent="space-between"
            alignItems="center"
          >
            {
              selectedCategory ? (
                <SelectedCategoryHeader
                  category={selectedCategory}
                  onBack={handleResetSelectedCategory}
                />
              ) : (
                <Typography component="h1" variant="h1">
                  {'Services'}
                </Typography>
              )
            }

            <Box>
              {
                selectedCategory ? (
                  <SubmitButton
                    title={'Add service'}
                    endIcon={<PlusIcon />}
                    onClick={handleOpenServicesDialog}
                  />
                ) : (<SubmitButton
                  title={'Add category'}
                  endIcon={<PlusIcon />}
                  onClick={handleOpenCreateCategoryModal}
                />)
              }
            </Box>
          </Box>
          <main className="main-page">
            <Box
              display="flex"
              style={{
                display: "flex",
                flex: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  padding: "24px 16px 0 16px",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #D6D6DB",
                    borderBottomLeftRadius: "8px",
                    borderTopLeftRadius: "8px",
                    borderRight: "none",
                    display: "flex",
                    alignItems: "center",
                    paddingX: 2,
                    paddingY: 1,
                    color: "#1F1F1F",
                    fontWeight: "500",
                  }}
                >
                  <ToggleBox
                    label={ServicesSearchTypes.SERVICE}
                    onSelected={handleChange}
                    selected={selectedToggleBox}
                  />
                  {
                    !selectedCategory ? (
                      <ToggleBox
                        label={ServicesSearchTypes.CATEGORY}
                        onSelected={handleChange}
                        selected={selectedToggleBox}
                      />
                    ) : null
                  }
                  <ToggleBox
                    label={ServicesSearchTypes.SUBCATEGORY}
                    onSelected={handleChange}
                    selected={selectedToggleBox}
                  />
                </Box>
                <Box
                  display="flex"
                  sx={{
                    border: "1px solid #D6D6DB",
                    backgroundColor: "#FAFAFA",
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px",
                    flex: 1,
                    maxWidth: "296px",
                  }}
                >
                  <InputBase
                    sx={{ ml: 2, flex: 1, fontSize: "14px" }}
                    placeholder="Search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <IconButton type="button" onClick={handleSearchClick}>
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                }}
              >

                <Box
                  sx={{
                    minWidth: 120,
                    margin: "24px 16px 0 0",
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="select-sort-by-label">Sort by</InputLabel>
                    <Select
                      labelId="select-sort-by-label"
                      id="select-sort-by"
                      value={sortBy}
                      label="Sort by"
                      onOpen={() => setIsSortSelectOpen(true)}
                      onClose={() => setIsSortSelectOpen(false)}
                      onChange={handleChangeSortBy}
                      IconComponent={() => (
                        <>
                          {isSortSelectOpen ? (
                            <ChevronUp fontSize="inherit" />
                          ) : (
                            <ChevronDown fontSize="inherit" />
                          )}
                        </>
                      )}
                      sx={{
                        borderRadius: "8px",
                        color: "#1F1F1F",
                        fontWeight: "500",
                        height: "48px",

                        "& .MuiSvgIcon-root": {
                          marginRight: "14px",
                          color: isSortSelectOpen ? "#BF295C" : "#676767",
                        },
                      }}
                      MenuProps={SortByMenuStyleProps}
                    >
                      <MenuItem disableRipple value={SortTypes.ID}>
                        {SortTypes.ID}
                      </MenuItem>
                      <MenuItem disableRipple value={SortTypes.NAME}>
                        {SortTypes.NAME}
                      </MenuItem>
                      {
                        selectedCategory ? (
                          <MenuItem disableRipple value={SortTypes.DURATION}>
                            {SortTypes.DURATION}
                          </MenuItem>
                        ) : null
                      }
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {
              selectedCategory ? (
                <Box sx={{
                  padding: "32px 16px",
                  paddingBottom: "16px",
                }}>
                  <SubCategoriesTable category={filteredSelectedCategory} />
                </Box>
              ) : (<CategoriesListSection
                onSelect={handleCategorySelect}
                onAddNewCategory={handleOpenCreateCategoryModal}
                data={filteredCategories}
              />)
            }
          </main>
        </>
      </MainPage>
    </>
  );
}
